<template>
    <div class="cst-wrapper">
        <!-- ═══════════════════════════════════════════
             HEADER / NAVBAR
        ══════════════════════════════════════════════ -->
        <nav class="cst-nav">

            <div class="cst-nav__inner">
                <div class="cst-nav__brand" @click="goToHome" role="button" tabindex="0" title="Home">
                    <img src="@/assets/img/bau_logo_new.png" alt="BAU Logo" class="cst-nav__logo" />
                </div>
                <div class="cst-nav__right">
                    <a href="https://bau.edu/academic-catalog/" target="_blank" rel="noopener noreferrer" class="cst-nav__link">
                        <i class="ri-calendar-line"></i>
                        <span>2026-2027 Academic Catalog</span>
                    </a>
                </div>
            </div>
        </nav>

        <!-- ═══════════════════════════════════════════
             MAIN CONTENT
        ══════════════════════════════════════════════ -->
        <div class="cst-page">

            <!-- Category / Badge Header -->
            <div class="cst-section-tag">
                <span class="cst-section-tag__line"></span>
                <span class="cst-section-tag__text">CLASS SCHEDULE</span>
            </div>

            <!-- Page Titles (Different for Search vs Results) -->
            <template v-if="!showResults">
                <div class="d-flex justify-content-between align-items-start mb-4">
                    <div>
                        <h1 class="cst-hero-title">Find the right classes for your semester</h1>
                        <p class="cst-hero-sub mb-0">
                            Refine the schedule by semester, program, modality, and day of the week.
                        </p>
                    </div>
                    <!-- Right Help Tooltip Card -->
                    <div class="cst-help-box" @click="scrollToModalityGuide" role="button" tabindex="0">
                        <i class="ri-question-line cst-help-box__icon"></i>
                        <span class="cst-help-box__text">
                            Not sure which format fits? Review the modality guide below.
                        </span>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="mb-4">
                    <h1 class="cst-hero-title">{{ selectedSemesterText }} Course Schedule</h1>
                    <p class="cst-hero-sub mb-0">
                        Review the courses that match your search, or adjust your filters below.
                    </p>
                </div>
            </template>

            <!-- ═══════════════════════════════════════════
                 FILTER BOX (Shared in Both Pages)
            ══════════════════════════════════════════════ -->
            <div class="cst-filter-box">
                <b-row align-v="center" class="mb-3">
                    <b-col>
                        <div class="cst-filter-box__heading">
                            <div class="cst-filter-box__icon-badge">
                                <i class="ri-equalizer-line"></i>
                            </div>
                            <div class="d-flex flex-column ml-2">
                                <span class="cst-filter-box__title">Search the schedule</span>
                                <span class="cst-filter-box__subtitle">
                                    Choose one or more filters to narrow available classes.
                                </span>
                            </div>
                        </div>
                    </b-col>
                </b-row>
                <ValidationObserver ref="filterForm">
                    <b-row class="cst-filter-row g-3">
                        <!-- Semester -->
                            <b-col cols="12" sm="6" md="3">
                                <ValidationProvider name="semester_id" rules="required" v-slot="{valid, errors}">

                                <div class="d-flex justify-content-between align-items-center mb-1">
                                    <label class="cst-label mb-0">Semester</label>
                                </div>
                                    <div class="cst-custom-select-wrap"
                                         :class="{'cst-custom-select-wrap--active':formData.semester_id}"
                                    >
                                        <i class="ri-calendar-event-line cst-select-icon"></i>
                                            <div class="cst-multiselect-container">
                                                    <semesters-selectbox
                                                        :active-semester-information="true"
                                                        @activeSemesterInformation="activeSemesterInformation"
                                                        @selected="selectedSemester"
                                                        v-model="formData.semester_id"
                                                        :is-public="true"
                                                        :set-active="true"
                                                    />
                                            </div>
                                    </div>
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]"></b-form-invalid-feedback>

                                </ValidationProvider>
                            </b-col>


                        <!-- Program -->
                        <b-col cols="12" sm="6" md="3">
                            <div class="d-flex justify-content-between align-items-center mb-1">
                                <label class="cst-label mb-0">Program</label>
                            </div>
                            <div class="cst-custom-select-wrap"
                                 :class="{'cst-custom-select-wrap--active':formData.program_code}"
                            >
                                <i class="ri-book-line cst-select-icon"></i>
                                <div class="cst-multiselect-container">
                                    <program-selectbox
                                        :is-active="true"
                                        level="L,YL"
                                        major_type="A"
                                        v-model="formData.program_code"
                                        :public="true"
                                        :formated-label="true"
                                        @selected="selectedProgram"
                                    />
                                </div>
                            </div>
                        </b-col>

                        <b-col cols="12" sm="6" md="3">
                            <div class="d-flex justify-content-between align-items-center mb-1">
                                <label class="cst-label mb-0">Modality</label>
                            </div>
                            <div class="cst-custom-select-wrap"
                                 :class="{'cst-custom-select-wrap--active':formData.theoretical_model}"
                            >
                                <i class="ri-computer-line cst-select-icon"></i>
                                <div class="cst-multiselect-container">
                                    <parameter-selectbox
                                        code="section_processing_types"
                                        :public="true"
                                        v-model="formData.theoretical_model"
                                    />
                                </div>
                            </div>
                        </b-col>

                        <!-- Day of Week -->
                        <b-col cols="12" sm="6" md="3">
                            <div class="d-flex justify-content-between align-items-center mb-1">
                                <label class="cst-label mb-0">Day of the Week</label>
                            </div>
                            <div class="cst-custom-select-wrap"
                                 :class="{'cst-custom-select-wrap--active':formData.day}"

                            >
                                <i class="ri-time-line cst-select-icon"></i>
                                <div class="cst-multiselect-container">
                                    <DaysSelectbox
                                        v-model="formData.day"
                                    />
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </ValidationObserver>


                <!-- Bottom Action & Status Bar -->
                <div class="cst-filter-bottom-bar mt-4 pt-3">
                    <div class="cst-status-msg">
                        <span class="cst-status-dot"></span>
                        <template v-if="!showResults">
                            <span class="cst-status-text">
                                {{ selectedSemesterText }} is ready to search
                            </span>
                        </template>
                        <template v-else>
                            <span class="cst-status-text">
                                Search complete · <strong>{{ datatable.total }}</strong> courses found for {{ selectedSemesterText }}
                            </span>
                        </template>
                    </div>

                    <div class="cst-action-btns">
                        <button class="cst-reset-btn" :disabled="loading" @click="resetFilters">
                            <i class="ri-restart-line"></i>
                            <span>Reset</span>
                        </button>
                        <button class="cst-search-btn" :disabled="loading" @click="searchSchedule">
                            <b-spinner v-if="loading" small class="mr-1"></b-spinner>
                            <i v-else class="ri-search-2-line"></i>
                            <span>{{ loading ? 'Searching...' : 'Search Classes' }}</span>
                            <i v-if="!loading" class="ri-arrow-right-line"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- ═══════════════════════════════════════════
                 SECTION 1: COURSE MODALITY GUIDE (Page 1)
            ══════════════════════════════════════════════ -->
            <div v-if="!showResults" id="modality-guide" class="cst-modality-guide-wrap mt-4">
                <div class="cst-modality-guide">
                    <!-- Dark Blue Header Banner -->
                    <div class="cst-modality-guide__banner">
                        <div class="d-flex align-items-center">
                            <div class="cst-modality-guide__banner-icon mr-3">
                                <i class="ri-book-open-line"></i>
                            </div>
                            <div>
                                <h3 class="cst-modality-guide__banner-title mb-0">Course modality guide</h3>
                                <p class="cst-modality-guide__banner-desc mb-0">
                                    Compare how and when each class format meets before choosing a modality.
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- 2x2 Grid + Bottom Full Width Card -->
                    <div class="cst-modality-cards p-4">
                        <b-row class="g-3">
                            <!-- 1. On Campus -->
                            <b-col cols="12" md="6" class="mb-3">
                                <div class="cst-guide-card">
                                    <div class="cst-guide-card__icon-box">
                                        <i class="ri-building-line"></i>
                                    </div>
                                    <span class="cst-guide-card__badge">CAMPUS</span>
                                    <h4 class="cst-guide-card__title">On Campus</h4>
                                    <p class="cst-guide-card__text">
                                        Courses are conducted in person on campus at scheduled days and times throughout the semester. Students are expected to attend scheduled class meetings in accordance with University and course attendance requirements.
                                    </p>
                                    <div class="cst-card-action">
                                        <button type="button" class="cst-card-search-btn" @click="searchByModality('In Class')">
                                            Search On-Campus Classes <i class="ri-arrow-right-line"></i>
                                        </button>
                                    </div>
                                </div>
                            </b-col>

                            <!-- 2. Hybrid (H) -->
                            <b-col cols="12" md="6" class="mb-3">
                                <div class="cst-guide-card">
                                    <div class="cst-guide-card__icon-box">
                                        <i class="ri-layout-masonry-line"></i>
                                    </div>
                                    <span class="cst-guide-card__badge">H · HS · HA</span>
                                    <h4 class="cst-guide-card__title">Hybrid (H)</h4>
                                    <p class="cst-guide-card__text">
                                        Courses combine scheduled in-person instruction with online instruction. Hybrid may be conducted in a Hybrid Synchronous (HS) or Hybrid Asynchronous (HA) modality.
                                    </p>
                                    <p class="cst-guide-card__text mt-2">
                                        Fifty percent (50%) of instruction is conducted in person at scheduled days and times throughout the semester. The remaining instruction is completed either synchronously or asynchronously (as determined in collaboration with the Department Chair and faculty) through the University Learning Management System (LMS).
                                    </p>
                                    <p class="cst-guide-card__text mt-2">
                                        Required in-person meeting dates and times must be established before the beginning of the course and communicated through the official course schedule and syllabus.
                                    </p>
                                    <div class="cst-card-action">
                                        <button type="button" class="cst-card-search-btn" @click="searchByModality('Hybrid')">
                                            Search Hybrid Classes <i class="ri-arrow-right-line"></i>
                                        </button>
                                    </div>
                                </div>
                            </b-col>

                            <!-- 3. Online Asynchronous (Online A) -->
                            <b-col cols="12" md="6" class="mb-3">
                                <div class="cst-guide-card">
                                    <div class="cst-guide-card__icon-box">
                                        <i class="ri-cloud-line"></i>
                                    </div>
                                    <span class="cst-guide-card__badge">ONLINE A</span>
                                    <h4 class="cst-guide-card__title">Online Asynchronous (Online A)</h4>
                                    <p class="cst-guide-card__text">
                                        Courses are conducted online primarily through asynchronous instruction.
                                    </p>
                                    <p class="cst-guide-card__text mt-2">
                                        The first class meeting is conducted synchronously using a University-approved virtual meeting platform, and student attendance at this meeting is required. The remainder of the course is completed asynchronously through the LMS with no regularly scheduled virtual meeting times.
                                    </p>
                                    <p class="cst-guide-card__text mt-2">
                                        Course activities, assignments, assessments, and deadlines are established by the instructor and must be completed according to the course schedule. Asynchronous instruction does not mean that a course is self-paced.
                                    </p>
                                    <div class="cst-card-action">
                                        <button type="button" class="cst-card-search-btn" @click="searchByModality('Online Async')">
                                            Search Online Asynchronous Classes <i class="ri-arrow-right-line"></i>
                                        </button>
                                    </div>
                                </div>
                            </b-col>

                            <!-- 4. Online Synchronous (Online S) -->
                            <b-col cols="12" md="6" class="mb-3">
                                <div class="cst-guide-card">
                                    <div class="cst-guide-card__icon-box">
                                        <i class="ri-video-line"></i>
                                    </div>
                                    <span class="cst-guide-card__badge">ONLINE S</span>
                                    <h4 class="cst-guide-card__title">Online Synchronous (Online S)</h4>
                                    <p class="cst-guide-card__text">
                                        Courses are conducted fully online through scheduled synchronous virtual class meetings using a University-approved virtual meeting platform.
                                        <br><br>
                                        Students are required to attend scheduled virtual class meetings in accordance with University and course attendance requirements. Meeting days and times must be established before the beginning of the course and communicated through the official course schedule and syllabus.
                                    </p>
                                    <div class="cst-card-action">
                                        <button type="button" class="cst-card-search-btn" @click="searchByModality('Online Sync')">
                                            Search Online Synchronous Classes <i class="ri-arrow-right-line"></i>
                                        </button>
                                    </div>
                                </div>
                            </b-col>

                            <!-- 5. Professional Hybrid (P-Hybrid) - Full Width -->
                            <b-col cols="12">
                                <div class="cst-guide-card cst-guide-card--pro">
                                    <div class="cst-guide-card__pro-left">
                                        <div class="cst-guide-card__icon-box">
                                            <i class="ri-briefcase-line"></i>
                                        </div>
                                        <span class="cst-guide-card__badge mt-2">P-HYBRID</span>
                                    </div>
                                    <div class="cst-guide-card__pro-content">
                                        <h4 class="cst-guide-card__title">Professional Hybrid (P-Hybrid)</h4>
                                        <p class="cst-guide-card__text">
                                            Courses combine a required, concentrated in-person residency with asynchronous online instruction.
                                        </p>
                                        <p class="cst-guide-card__text mt-2">
                                            Students attend a minimum of one scheduled residency weekend per semester for each P-Hybrid course. Each residency consists of ten (10) hours of required in-person instruction per course. The remainder of the course is completed asynchronously through the LMS with no regularly scheduled virtual meeting times.
                                        </p>

                                        <!-- Residency Schedules & Time Slots -->
                                        <div class="cst-pro-section">
                                            <h5 class="cst-pro-subtitle">Residency Schedules &amp; Time Slots</h5>
                                            <p class="cst-guide-card__text">
                                                Both graduate and undergraduate courses follow standard 10-hour residency weekend schedules divided into two possible time slots:
                                            </p>
                                            <div class="cst-pro-slots">
                                                <div class="cst-slot-box">
                                                    <div class="cst-slot-box__title">Time Slot A:</div>
                                                    <ul class="cst-slot-list">
                                                        <li><strong>Friday:</strong> 12:00 PM – 3:00 PM (3 hours)</li>
                                                        <li><strong>Saturday:</strong> 8:00 AM – 12:00 PM (4 hours)</li>
                                                        <li><strong>Sunday:</strong> 8:00 AM – 11:00 AM (3 hours)</li>
                                                    </ul>
                                                </div>
                                                <div class="cst-slot-box">
                                                    <div class="cst-slot-box__title">Time Slot B:</div>
                                                    <ul class="cst-slot-list">
                                                        <li><strong>Friday:</strong> 4:00 PM – 7:00 PM (3 hours)</li>
                                                        <li><strong>Saturday:</strong> 1:00 PM – 5:00 PM (4 hours)</li>
                                                        <li><strong>Sunday:</strong> 12:00 PM – 3:00 PM (3 hours)</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Scheduling & Modality Codes Accordion -->
                                        <div class="cst-pro-section mt-3">
                                            <button
                                                type="button"
                                                class="cst-toggle-btn"
                                                @click="showPHybridCodes = !showPHybridCodes"
                                                :aria-expanded="showPHybridCodes"
                                            >
                                                <span>{{ showPHybridCodes ? 'Hide P-Hybrid Modality Codes ▴' : 'View P-Hybrid Modality Codes ▾' }}</span>
                                            </button>

                                            <b-collapse v-model="showPHybridCodes" id="p-hybrid-codes-collapse" class="mt-3">
                                                <h5 class="cst-pro-subtitle">Scheduling &amp; Modality Codes</h5>
                                                <p class="cst-guide-card__text">
                                                    Modality codes display on course schedules to identify the academic level, residency weekend sequence, and instructional time slot:
                                                </p>
                                                <ul class="cst-pro-bullets">
                                                    <li><strong>Graduate Level:</strong> Courses currently utilize one residency weekend per semester.</li>
                                                    <li><strong>Undergraduate Level:</strong> Courses may be scheduled across up to two residency weekends (Weekend 1 and Weekend 2).</li>
                                                </ul>

                                                <div class="cst-pro-table-wrap">
                                                    <table class="cst-pro-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Modality Code</th>
                                                                <th>Level</th>
                                                                <th>Weekend</th>
                                                                <th>Time Slot</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td><span class="cst-code-text">P-Hybrid G-1a</span></td>
                                                                <td>Graduate</td>
                                                                <td>Weekend 1</td>
                                                                <td>Slot A</td>
                                                            </tr>
                                                            <tr>
                                                                <td><span class="cst-code-text">P-Hybrid G-1b</span></td>
                                                                <td>Graduate</td>
                                                                <td>Weekend 1</td>
                                                                <td>Slot B</td>
                                                            </tr>
                                                            <tr>
                                                                <td><span class="cst-code-text">P-Hybrid UG-1a</span></td>
                                                                <td>Undergraduate</td>
                                                                <td>Weekend 1</td>
                                                                <td>Slot A</td>
                                                            </tr>
                                                            <tr>
                                                                <td><span class="cst-code-text">P-Hybrid UG-1b</span></td>
                                                                <td>Undergraduate</td>
                                                                <td>Weekend 1</td>
                                                                <td>Slot B</td>
                                                            </tr>
                                                            <tr>
                                                                <td><span class="cst-code-text">P-Hybrid UG-2a</span></td>
                                                                <td>Undergraduate</td>
                                                                <td>Weekend 2</td>
                                                                <td>Slot A</td>
                                                            </tr>
                                                            <tr>
                                                                <td><span class="cst-code-text">P-Hybrid UG-2b</span></td>
                                                                <td>Undergraduate</td>
                                                                <td>Weekend 2</td>
                                                                <td>Slot B</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </b-collapse>
                                        </div>

                                        <div class="cst-card-action">
                                            <button type="button" class="cst-card-search-btn" @click="searchByModality('Professional Hybrid 1')">
                                                Search Professional Hybrid Classes <i class="ri-arrow-right-line"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </b-col>
                        </b-row>
                    </div>

                    <!-- Modality Info Footer Bar -->
                    <div class="cst-modality-info-bar">
                        <i class="ri-information-line"></i>
                        <span>Please confirm required meeting dates and attendance policies in your official syllabus and course schedule.</span>
                    </div>
                </div>

                <!-- Page 1 Bottom Advisor Note -->
                <div class="cst-advisor-note mt-3">
                    <i class="ri-question-answer-line"></i>
                    <span>Have questions regarding course requirements or schedule planning? Contact your academic advisor prior to registration.</span>
                </div>
            </div>

            <div v-else class="cst-results-container mt-4">
                <!-- Loading State Overlay / Skeleton -->
                <div v-if="loading" class="cst-table-loading-card">
                    <div class="cst-spinner-circle">
                        <b-spinner variant="primary" style="width: 2.4rem; height: 2.4rem;"></b-spinner>
                    </div>
                    <h4 class="cst-loading-title">Searching the schedule...</h4>
                    <p class="cst-loading-desc">Fetching available courses and section details for {{ selectedSemesterText }}.</p>
                    <div class="cst-loading-shimmer-wrap mt-4">
                        <div class="cst-loading-shimmer-line cst-loading-shimmer-line--wide"></div>
                        <div class="cst-loading-shimmer-line cst-loading-shimmer-line--med"></div>
                    </div>
                </div>

                <!-- Table Content (When not loading) -->
                <div v-else class="cst-results-content">
                    <!-- Results Header Bar -->
                    <div class="d-flex justify-content-between align-items-end mb-2">
                        <div>
                            <div class="d-flex align-items-center">
                                <h2 class="cst-results-heading mb-0">{{ datatable.total }} courses found</h2>
                                <span class="cst-results-semester-pill ml-2">{{ selectedSemesterText }}</span>
                            </div>
                            <p class="cst-results-subtext mb-0 mt-1">
                                {{ filterSummaryText }}
                            </p>
                        </div>
                        <span class="cst-results-counter">
                            {{ resultsCounterText }}
                        </span>
                    </div>

                    <!-- Table Component (BootstrapVue b-table with Pagination) -->
                    <div class="cst-table-wrap mt-2">
                        <b-table
                            :items="datatable.rows"
                            :fields="tableFields"
                            :busy="datatable.isLoading"
                            responsive
                            hover
                            show-empty
                            empty-text="No courses found matching your filters."
                            class="cst-table mb-0"
                        >
                            <template #head(re)="data">
                                <span
                                    v-b-tooltip.hover
                                    title="R = Required course, E = Elective course"
                                    class="cst-re-header-tooltip"
                                >
                                    {{ data.label }}
                                </span>
                            </template>
                        </b-table>

                        <!-- Pagination Footer -->
                        <div class="cst-table-footer">
                            <div class="cst-table-footer__left">
                                <b-form-select
                                    v-model="datatable.queryParams.limit"
                                    :options="perPageOptions"
                                    size="sm"
                                    class="cst-per-page-select"
                                    @change="onBPerPageChange"
                                />
                            </div>
                            <div class="cst-table-footer__right">
                                <span class="cst-pagination-info">{{ paginationRangeText }}</span>
                                <b-pagination
                                    v-model="datatable.queryParams.page"
                                    :total-rows="datatable.total"
                                    :per-page="datatable.queryParams.limit"
                                    size="sm"
                                    class="cst-b-pagination mb-0"
                                    @change="onBPageChange"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Legend for R/E* -->
                    <div class="cst-table-legend mt-2 mb-2">
                        <i class="ri-information-line mr-1"></i>
                        <span><strong>* R/E:</strong> R = Required course, E = Elective course</span>
                    </div>

                <!-- Page 2 Bottom Advisor Note -->
                <div class="cst-advisor-note mt-3">
                    <i class="ri-question-answer-line"></i>
                    <span>Have questions regarding course requirements or schedule planning? Contact your academic advisor prior to registration.</span>
                </div>
            </div> <!-- /.cst-results-content -->

        </div> <!-- /.cst-results-container -->

    </div> <!-- /.cst-page -->

</div> <!-- /.cst-wrapper -->
</template>

<script>
import CourseScheduleService from '@/services/CourseScheduleService';
import DaysSelectbox from '@/components/interactive-fields/DaysSelectbox.vue';
import qs from "qs";

export default {
    name: 'ClassScheduleToolIndex',
    components: {
        DaysSelectbox,
    },
    metaInfo() {
        return {
            title: this.$t('Class Schedule')
        }
    },

    data() {
        return {
            /* ── UI state ─────────────────────────── */
            showResults: false,
            loading: false,
            showPHybridCodes: false,
            semesterInformation: null,
            selectedSemesterInformation: null,
            selectedProgramInformation: null,
            perPageOptions: [5, 10, 15, 20, 40, 50],

            /* ── Form Data (Bound to requested selectboxes) ── */
            formData: {
                semester_id: null,
                program_code: null,
                theoretical_model: null,
                day: null,
            },

            /* ── Table State ── */
            tableFields: [
                {
                    key: 'program',
                    label: 'PROGRAM',
                    tdClass: 'cst-td-program',
                },
                {
                    key: 'course_code',
                    label: 'COURSE CODE',
                    tdClass: 'cst-td-code',
                },
                {
                    key: 're',
                    label: 'R/E*',
                    tdClass: 'cst-td-re',
                },
                {
                    key: 'prerequisite',
                    label: 'PRE-REQUISITE',
                    tdClass: 'cst-td-muted',
                },
                {
                    key: 'course_name',
                    label: 'COURSE NAME',
                    tdClass: 'cst-td-text',
                },
                {
                    key: 'faculty',
                    label: 'FACULTY',
                    tdClass: 'cst-td-text',
                },
                {
                    key: 'day',
                    label: 'DAY',
                    tdClass: 'cst-td-muted',
                },
                {
                    key: 'start',
                    label: 'START',
                    tdClass: 'cst-td-muted',
                },
                {
                    key: 'end',
                    label: 'END',
                    tdClass: 'cst-td-muted',
                },
                {
                    key: 'classroom',
                    label: 'CLASSROOM',
                    tdClass: 'cst-td-muted',
                },
                {
                    key: 'modality',
                    label: 'MODALITY',
                    tdClass: 'cst-td-text',
                },
            ],

            datatable: {
                isLoading: false,
                showTable: true,
                rows: [],
                total: 0,
                queryParams: {
                    filter: {},
                    page: 1,
                    limit: 15,
                    sort: '-id',
                },
            },
        };
    },

    computed: {
        selectedSemesterText() {
            let semesterText;
            if (this.selectedSemesterInformation?.text) {
                semesterText = this.selectedSemesterInformation.text;
            } else if (this.semesterInformation?.name) {
                semesterText = this.semesterInformation.name;
            } else if (this.semesterInformation?.academic_year && this.semesterInformation?.semester_text) {
                semesterText = `${this.semesterInformation.academic_year} ${this.semesterInformation.semester_text}`;
            }
            return semesterText || '';
        },
        filterSummaryText() {
            let programText = 'All programs';
            if (this.selectedProgramInformation?.text) {
                programText = this.selectedProgramInformation.text;
            } else if (this.selectedProgramInformation?.name) {
                programText = this.selectedProgramInformation.name;
            } else if (this.formData.program_code) {
                const found = this.datatable.rows?.find(r => r.program_code === this.formData.program_code || (r.program && r.program !== '—'));
                if (found && found.program && found.program !== '—') {
                    programText = found.program;
                } else {
                    programText = this.formData.program_code;
                }
            }
            // Strip any numeric table code prefix (e.g. 0105003 - MS in Data Science -> MS in Data Science)
            programText = programText.replace(/^\d+\s*[-·:]\s*/, '').trim();
            if (/^\d+$/.test(programText)) {
                const matchedRow = this.datatable.rows?.find(r => r.program && r.program !== '—');
                programText = matchedRow ? matchedRow.program : 'All programs';
            }

            const modalityText = this.formData.theoretical_model || 'All modalities';
            const dayText = this.getDayName(this.formData.day);
            return `${programText} · ${modalityText} · ${dayText}`;
        },
        resultsCounterText() {
            if (this.datatable.total === 0) return '0 courses';
            const start = (this.datatable.queryParams.page - 1) * this.datatable.queryParams.limit + 1;
            const end = Math.min(this.datatable.queryParams.page * this.datatable.queryParams.limit, this.datatable.total);
            return `Showing ${start}–${end} of ${this.datatable.total} courses`;
        },
        paginationRangeText() {
            if (this.datatable.total === 0) return '0 of 0';
            const start = (this.datatable.queryParams.page - 1) * this.datatable.queryParams.limit + 1;
            const end = Math.min(this.datatable.queryParams.page * this.datatable.queryParams.limit, this.datatable.total);
            return `${start}–${end} of ${this.datatable.total}`;
        },
        results() {
            return this.datatable.rows || [];
        },
    },

    methods: {
        goToHome() {
            this.showResults = false;
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        scrollToModalityGuide() {
            const el = document.getElementById('modality-guide');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
            }
        },
        async searchByModality(modalityCode) {
            if (!this.formData.semester_id && this.selectedSemesterInformation?.value) {
                this.formData.semester_id = this.selectedSemesterInformation.value;
            }
            this.formData.theoretical_model = modalityCode;
            await this.searchSchedule();
            window.scrollTo({ top: 350, behavior: 'smooth' });
        },
        selectedSemester(row) {
            this.selectedSemesterInformation = row;
        },
        selectedProgram(row) {
            this.selectedProgramInformation = row;
        },
        getDayName(day) {
            if (!day) return 'Any day';
            const days = {
                1: 'Monday',
                2: 'Tuesday',
                3: 'Wednesday',
                4: 'Thursday',
                5: 'Friday',
                6: 'Saturday',
                7: 'Sunday',
            };
            return days[day] || 'Any day';
        },
        formatTo12Hour(timeStr) {
            if (!timeStr || timeStr === '—' || timeStr === '-') return '—';
            const match = String(timeStr).trim().match(/^(\d{1,2}):(\d{2})/);
            if (!match) return timeStr;
            let hours = parseInt(match[1], 10);
            const minutes = match[2];
            if (isNaN(hours)) return timeStr;
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            return `${hours}:${minutes} ${ampm}`;
        },
        async searchSchedule() {
            const isValid = await this.$refs.filterForm.validate();
            if (!isValid) return;
            this.datatable.queryParams.page = 1;
            this.datatable.queryParams.filter = {
                semester_id:       this.formData.semester_id,
                program_code:      this.formData.program_code,
                theoretical_model: this.formData.theoretical_model,
                day:               this.formData.day,
            };
            this.showResults = true;
            await this.getRows();
        },

        async getRows() {
            const isValid = await this.$refs.filterForm.validate();
            if (!isValid) return;
            this.loading = true;
            this.datatable.isLoading = true;
            this.datatable.showTable = true;

            const config = {
                params: {
                   ...this.datatable.queryParams
                },
                paramsSerializer: (params) => qs.stringify(params, { encode: false })
            };

            try {
                const response = await CourseScheduleService.getPublic(config);
                const rawData = response.data?.data || [];
                this.datatable.rows = rawData.map(item => this.mapApiItem(item));
                this.datatable.total = response.data?.pagination?.total ?? rawData.length;
            } catch (error) {
                this.showErrors(error, null);
            } finally {
                this.loading = false;
                this.datatable.isLoading = false;
            }
        },

        /** Pagination page change (b-pagination) */
        onBPageChange(page) {
            this.datatable.queryParams.page = page;
            this.getRows();
            window.scrollTo({ top: 350, behavior: 'smooth' });
        },

        /** Pagination per-page limit change (b-form-select) */
        onBPerPageChange(limit) {
            this.datatable.queryParams.limit = Number(limit);
            this.datatable.queryParams.page = 1;
            this.getRows();
        },

        /** Map backend item to table fields */
        mapApiItem(item) {
            const facultyName = item.instructor ? `${item.instructor.name || ''} ${item.instructor.surname || ''}`.trim() : null;
            const dayName = item.day_name;
            const startTime = item.start_time;
            const endTime = item.end_time;
            const cleanProgram = item.program?.name ? item.program.name.replace(/^\d+\s*[-·:]\s*/, '').trim() : (item.program || '—');
            const typeValue = item.type || (item.is_required === true || item.is_required === 1 ? 'Required' : (item.is_required === false || item.is_required === 0 ? 'Elective' : 'Required'));

            return {
                ...item,
                id:           item.id,
                program:      cleanProgram || '—',
                course_code:  item.course_code || item.course?.code || item.code || '—',
                type:         typeValue,
                re:           item.re || typeValue,
                prerequisite: item.prerequisites || '—',
                course_name:  item.course_name || item.course?.name || item.name || '—',
                faculty:      facultyName || '—',
                day:          dayName || '—',
                start:        this.formatTo12Hour(startTime),
                end:          this.formatTo12Hour(endTime),
                classroom:    item.classroom?.name || '—',
                modality:     item.modality || '—',
            };
        },

        activeSemesterInformation(event) {
            this.semesterInformation = event;
            if (event) {
                this.selectedSemesterInformation = {
                    value: event.id,
                    text: event.name || `${event.academic_year} ${event.semester_text}`,
                };
                if (!this.formData.semester_id) {
                    this.formData.semester_id = event.id;
                }
            }
        },
        /** Reset handler */
        resetFilters() {
            const defaultSemesterId = this.selectedSemesterInformation ? this.selectedSemesterInformation.value : (this.semesterInformation?.id || null);
            this.formData = {
                semester_id:       defaultSemesterId,
                program_code:      null,
                theoretical_model: null,
                day:               null,
            };
            this.selectedProgramInformation = null;
            this.datatable.queryParams.filter = {};
            this.datatable.queryParams.page = 1;
            this.datatable.rows = [];
            this.datatable.total = 0;
            this.showResults = false;
        },
    },
};
</script>

<style lang="scss" scoped>

/* ════════════════════════════════════════════════
   THEME COLORS & VARIABLES
════════════════════════════════════════════════ */
$navy-dark:    #162544;
$navy-main:    #1A2C54;
$gold-badge:   #DBA631;
$bg-main:      #F8FAFC;
$card-border:  #E2E8F0;
$text-dark:    #0F172A;
$text-muted:   #64748B;
$amber-border: #D97706;

.cst-wrapper {
    min-height: 100vh;
    background: #FFFFFF;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: $text-dark;
}

/* ════════════════════════════════════════════════
   NAVBAR
════════════════════════════════════════════════ */
.cst-nav {
    background: $navy-dark;
    padding: 0 40px;
    height: 64px;
    display: flex;
    align-items: center;

    &__inner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        max-width: 1240px;
        margin: 0 auto;
    }

    &__brand {
        display: flex;
        align-items: center;
        cursor: pointer;
        transition: opacity 0.2s ease;

        &:hover {
            opacity: 0.85;
        }
    }

    &__logo {
        height: 200px;
        width: auto;
        object-fit: contain;
    }

    &__right {
        display: flex;
        align-items: center;
    }

    &__link {
        color: #FFFFFF;
        font-size: 13px;
        font-weight: 500;
        text-decoration: none;
        display: flex;
        align-items: center;
        gap: 8px;

        i {
            font-size: 16px;
            color: $gold-badge;
        }

        &:hover {
            color: #F8FAFC;
            text-decoration: underline;
        }
    }
}

/* ════════════════════════════════════════════════
   PAGE LAYOUT
════════════════════════════════════════════════ */
.cst-page {
    max-width: 1200px;
    margin: 0 auto;
    padding: 36px 32px 80px;
}

/* Section Tag: Line + CLASS SCHEDULE */
.cst-section-tag {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;

    &__line {
        display: inline-block;
        background: $gold-badge;
        width: 22px;
        height: 3px;
        border-radius: 2px;
    }

    &__text {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.8px;
        color: #1E293B;
    }
}

.cst-hero-title {
    font-size: 32px;
    font-weight: 800;
    color: #111E38;
    line-height: 1.2;
    margin-bottom: 6px;
}

.cst-hero-sub {
    font-size: 18px;
    color: #64748B;
    line-height: 1.5;
}

/* Right Tooltip Card */
.cst-help-box {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #EEF2F6;
    border: 1px solid transparent;
    border-radius: 8px;
    padding: 10px 16px;
    max-width: 280px;
    flex-shrink: 0;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: #E2E8F0;
        border-color: #CBD5E1;
        box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
        transform: translateY(-1px);

        .cst-help-box__icon {
            color: #1E293B;
        }

        .cst-help-box__text {
            color: #0F172A;
        }
    }

    &__icon {
        font-size: 18px;
        color: #475569;
        flex-shrink: 0;
        transition: color 0.2s ease;
    }

    &__text {
        font-size: 12px;
        color: #334155;
        line-height: 1.35;
        transition: color 0.2s ease;
    }
}

/* ════════════════════════════════════════════════
   FILTER BOX
════════════════════════════════════════════════ */
.cst-filter-box {
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    padding: 22px 28px 18px;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
    border-top: 4px solid $navy-dark;

    &__heading {
        display: flex;
        align-items: center;
    }

    &__icon-badge {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        border-radius: 8px;
        background: #EEF3FA;
        color: $navy-dark;
        font-size: 20px;
    }

    &__title {
        font-weight: 700;
        font-size: 18px;
        color: #0F172A;
        line-height: 1.2;
    }

    &__subtitle {
        font-size: 13px;
        color: #64748B;
        margin-top: 2px;
    }
}

.cst-filter-row {
    margin-top: 16px;
}

.cst-label {
    font-size: 12px;
    font-weight: 600;
    color: #1E293B;
}

.cst-selected-tag {
    font-size: 11px;
    font-weight: 600;
    color: $amber-border;
}

.cst-custom-select-wrap {
    position: relative;
    display: flex;
    align-items: center;
    background: #FFFFFF;
    border: 1.5px solid #CBD5E1;
    border-radius: 8px;
    padding: 0 10px 0 12px;
    height: 44px;
    transition: all 0.2s ease;
    width: 100%;

    &--active {
        border-color: $amber-border;
    }

    &:focus-within {
        border-color: $amber-border;
        box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.12);
    }

    .cst-select-icon {
        font-size: 18px;
        color: #334155;
        margin-right: 10px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
    }

    .cst-custom-select {
        border: none !important;
        background: transparent !important;
        box-shadow: none !important;
        padding: 0 !important;
        height: 100%;
        font-size: 13.5px;
        font-weight: 500;
        color: #0F172A;
        width: 100%;
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        cursor: pointer;

        &:focus {
            outline: none;
        }
    }

    .cst-multiselect-container {
        flex: 1;
        min-width: 0;
        height: 100%;
        display: flex;
        align-items: center;

        > div {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
        }

        ::v-deep .multiselect {
            width: 100%;
            height: 100%;
            min-height: 44px !important;
            max-height: 44px !important;
            border: none;
            background: transparent;
            display: flex;
            align-items: center;

            .multiselect__tags {
                border: none !important;
                background: transparent !important;
                padding: 0 28px 0 0 !important;
                min-height: 44px !important;
                height: 44px !important;
                box-shadow: none !important;
                width: 100%;
                display: flex !important;
                align-items: center !important;
            }

            .multiselect__single {
                background: transparent !important;
                font-size: 14px !important;
                font-weight: 500 !important;
                color: #0F172A !important;
                margin: 0 !important;
                padding: 0 !important;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                line-height: 1 !important;
                display: block !important;
            }

            .multiselect__input {
                font-size: 14px !important;
                margin: 0 !important;
                padding: 0 !important;
                background: transparent !important;
                line-height: 1 !important;
            }

            .multiselect__placeholder {
                color: #94A3B8 !important;
                font-size: 14px !important;
                padding: 0 !important;
                margin: 0 !important;
                line-height: 1 !important;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                display: block !important;
            }

            .multiselect__select {
                top: 50%;
                transform: translateY(-50%);
                width: 28px;
                height: 28px;
                right: 0;
                padding: 0;
                display: flex;
                align-items: center;
                justify-content: center;

                &:before {
                    border-color: #64748B transparent transparent;
                    border-width: 5px 5px 0 5px;
                    margin-top: 0;
                }
            }

            /* Dropdown list (Geniş ve Net Okunabilir) */
            .multiselect__content-wrapper {
                min-width: 320px;
                width: max-content;
                max-width: 440px;
                max-height: 300px;
                border-radius: 8px;
                border: 1px solid #CBD5E1;
                box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
                margin-top: 6px;
                background: #FFFFFF;
                z-index: 1050;
                left: -32px;

                .multiselect__option {
                    padding: 10px 14px;
                    font-size: 13px;
                    line-height: 1.45;
                    white-space: normal;
                    word-break: break-word;
                    color: #1E293B;

                    span {
                        white-space: normal !important;
                        overflow: visible !important;
                        text-overflow: clip !important;
                        display: block !important;
                        -webkit-line-clamp: unset !important;
                    }

                    &--highlight {
                        background: #F1F5F9;
                        color: #0F172A;
                        font-weight: 600;
                    }

                    &--selected {
                        background: #FFF7DF;
                        color: #8D5B00;
                        font-weight: 700;
                    }
                }
            }
        }
    }

    .cst-select-chevron {
        font-size: 18px;
        color: #475569;
        margin-left: 6px;
        pointer-events: none;
        flex-shrink: 0;
    }
}

/* Bottom Bar */
.cst-filter-bottom-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid #F1F5F9;
    flex-wrap: wrap;
    gap: 12px;
}

.cst-status-msg {
    display: inline-flex;
    align-items: center;
    gap: 8px;

    .cst-status-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #EAB308;
        display: inline-block;
    }

    .cst-status-text {
        font-size: 13px;
        color: #475569;
    }
}

.cst-action-btns {
    display: inline-flex;
    align-items: center;
    gap: 12px;

    .cst-reset-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: transparent;
        border: none;
        color: #1E293B;
        font-weight: 600;
        font-size: 13.5px;
        padding: 8px 12px;
        cursor: pointer;

        i {
            font-size: 16px;
        }

        &:hover {
            color: #0F172A;
        }
    }

    .cst-search-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: $navy-dark;
        color: #FFFFFF;
        border: none;
        font-weight: 600;
        font-size: 13.5px;
        padding: 9px 20px;
        border-radius: 6px;
        cursor: pointer;
        transition: background 0.15s ease;

        i {
            font-size: 16px;
        }

        .ri-arrow-right-line {
            color: $gold-badge;
        }

        &:hover {
            background: #0D172A;
        }
    }
}

/* ════════════════════════════════════════════════
   PAGE 1: COURSE MODALITY GUIDE
════════════════════════════════════════════════ */
.cst-modality-guide-wrap {
    margin-top: 24px;
}

.cst-modality-guide {
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    overflow: hidden;

    &__banner {
        background: #172B54;
        color: #FFFFFF;
        padding: 20px 24px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 16px;

        &-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;
            border-radius: 6px;
            border: 1px solid rgba(255, 255, 255, 0.25);
            font-size: 18px;
            color: $gold-badge;
        }

        &-title {
            font-size: 17px;
            font-weight: 700;
            color: #FFFFFF;
        }

        &-desc {
            font-size: 13px;
            color: #CBD5E1;
            margin-top: 2px;
        }
    }
}

.cst-guide-card {
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    padding: 20px 22px;
    height: 100%;
    display: flex;
    flex-direction: column;

    &__icon-box {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border-radius: 8px;
        background: #F1F5F9;
        color: #1E293B;
        font-size: 19px;
        margin-bottom: 12px;
    }

    &__badge {
        display: block;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.8px;
        color: #B45309;
        margin-bottom: 6px;
    }

    &__title {
        font-size: 16px;
        font-weight: 700;
        color: #0F172A;
        margin-bottom: 10px;
    }

    &__text {
        font-size: 16px;
        color: #475569;
        line-height: 1.6;
        margin: 0;
        flex: 1;
    }

    /* P-Hybrid full-width layout */
    &--pro {
        flex-direction: row;
        gap: 20px;
        align-items: flex-start;
    }

    &__pro-left {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex-shrink: 0;
        min-width: 70px;
    }

    &__pro-content {
        flex: 1;
    }
}

.cst-card-action {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #F1F5F9;

    &--pro {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 12px;
        margin-top: 18px;
        padding-top: 14px;
    }
}

.cst-card-search-btn {
    background: transparent;
    border: none;
    padding: 0;
    color: #172B54;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: color 0.15s ease, transform 0.15s ease;

    &:hover {
        color: #B45309;
        text-decoration: underline;
    }
}

.cst-toggle-btn {
    background: #F1F5F9;
    border: 1px solid #CBD5E1;
    border-radius: 6px;
    padding: 8px 14px;
    color: #1E293B;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
        background: #E2E8F0;
        border-color: #94A3B8;
    }
}

.cst-pro-subtitle {
    font-size: 16px;
    font-weight: 700;
    color: #0F172A;
    margin-top: 18px;
    margin-bottom: 8px;
}

.cst-pro-slots {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 14px;
    margin-top: 12px;
}

.cst-slot-box {
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    padding: 14px 16px;

    &__title {
        font-size: 15px;
        font-weight: 700;
        color: #1E293B;
        margin-bottom: 8px;
    }
}

.cst-slot-list {
    list-style: none;
    padding: 0;
    margin: 0;
    font-size: 14px;
    color: #334155;
    line-height: 1.8;
}

.cst-pro-bullets {
    padding-left: 20px;
    margin: 10px 0;
    font-size: 15px;
    color: #334155;
    line-height: 1.7;
}

.cst-pro-table-wrap {
    overflow-x: auto;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    margin-top: 12px;
}

.cst-pro-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;

    th {
        background: #F1F5F9;
        color: #0F172A;
        font-weight: 800;
        padding: 10px 14px;
        text-align: left;
        border-bottom: 1px solid #E2E8F0;
        white-space: nowrap;
    }

    td {
        padding: 10px 14px;
        color: #334155;
        border-bottom: 1px solid #F1F5F9;
        vertical-align: middle;
    }

    tr:last-child td {
        border-bottom: none;
    }
}

.cst-code-text {
    color: #0F172A;
    font-weight: 700;
    font-size: 14px;
    white-space: nowrap;
}

.cst-table-legend {
    padding: 12px 20px;
    background: #F8FAFC;
    border-top: 1px solid #E2E8F0;
    font-size: 13px;
    color: #475569;
    display: flex;
    align-items: center;
    gap: 6px;
}

.cst-modality-info-bar {
    background: #E8EEF8;
    color: #1E293B;
    font-size: 15px;
    padding: 14px 24px;
    display: flex;
    align-items: center;
    gap: 10px;

    i {
        font-size: 18px;
        color: #3B82F6;
    }
}

.cst-advisor-note {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 15px;
    color: #64748B;
    padding: 6px 4px;

    i {
        font-size: 18px;
    }
}

/* ════════════════════════════════════════════════
   PAGE 2: SEARCH RESULTS
════════════════════════════════════════════════ */
.cst-results-container {
    margin-top: 28px;
}

.cst-results-heading {
    font-size: 24px;
    font-weight: 800;
    color: #0F172A;
}

.cst-results-semester-pill {
    background: #FFF7DF;
    color: #8D5B00;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 4px;
}

.cst-results-subtext {
    font-size: 15px;
    color: #64748B;
}

.cst-results-counter {
    font-size: 14px;
    color: #64748B;
}

/* Results Table */
.cst-table-wrap {
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
    overflow: hidden;
}

.cst-re-header-tooltip {
    cursor: help;
    border-bottom: 1px dotted #64748B;
    padding-bottom: 1px;
}

.cst-table-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    border-top: 1px solid #E2E8F0;
    background: #FAFAFA;
    padding: 12px 16px;
    font-size: 13px;
    color: #64748B;

    &__left {
        display: flex;
        align-items: center;
    }

    &__right {
        display: flex;
        align-items: center;
        gap: 14px;
    }
}

.cst-per-page-select {
    width: auto;
    min-width: 68px;
    font-size: 13px;
    color: #334155;
    border-color: #CBD5E1;
    border-radius: 6px;
}

.cst-pagination-info {
    font-size: 13px;
    font-weight: 500;
    color: #475569;
    white-space: nowrap;
}

::v-deep .cst-b-pagination {
    .page-link {
        color: #1E293B;
        border-color: #E2E8F0;
        font-size: 12.5px;
        padding: 4px 10px;
    }

    .page-item.active .page-link {
        background-color: #172B54;
        border-color: #172B54;
        color: #FFFFFF;
    }
}

::v-deep .cst-table {
    margin: 0;
    width: 100%;

    thead th {
        background: #F8FAFC !important;
        border-bottom: 2px solid #CBD5E1 !important;
        border-top: none !important;
        border-left: none !important;
        border-right: none !important;
        font-size: 12px !important;
        font-weight: 800 !important;
        letter-spacing: 0.6px !important;
        color: #0F172A !important;
        padding: 13px 14px !important;
        white-space: nowrap !important;
        vertical-align: middle !important;
    }

    tbody td {
        font-size: 13.5px !important;
        padding: 14px 14px !important;
        vertical-align: middle !important;
        border-top: none !important;
        border-left: none !important;
        border-right: none !important;
        border-bottom: 1px solid #F1F5F9 !important;
    }

    tbody tr:last-child td {
        border-bottom: none !important;
    }

    tbody tr:hover {
        background: #F8FAFC !important;
    }
}

.cst-td-program {
    font-size: 13px;
    font-weight: 600;
    color: #0F172A;
}

.cst-td-code {
    font-size: 13px;
    font-weight: 700;
    color: #0F172A;
    white-space: nowrap;
}

.cst-td-re {
    font-size: 12.5px;
    font-weight: 600;
    color: #0F172A;
}

.cst-td-text {
    font-size: 13px;
    color: #0F172A;
}

.cst-td-muted {
    font-size: 13px;
    color: #64748B;
    white-space: nowrap;
}

/* ════════════════════════════════════════════════
   ELEGANT LOADING STATE
════════════════════════════════════════════════ */
.cst-table-loading-card {
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    padding: 56px 24px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 20px rgba(15, 23, 42, 0.04);
    min-height: 320px;
    position: relative;
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, #1E40AF 0%, #3B82F6 50%, #60A5FA 100%);
        background-size: 200% 100%;
        animation: cst-gradient-flow 1.5s infinite linear;
    }
}

@keyframes cst-gradient-flow {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}

.cst-spinner-circle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 68px;
    height: 68px;
    border-radius: 50%;
    background: #EFF6FF;
    color: #1E40AF;
    box-shadow: 0 0 0 6px rgba(239, 246, 255, 0.8);
    animation: cst-pulse-ring 2s infinite ease-in-out;
}

@keyframes cst-pulse-ring {
    0%, 100% {
        box-shadow: 0 0 0 6px rgba(239, 246, 255, 0.8);
    }
    50% {
        box-shadow: 0 0 0 14px rgba(219, 234, 254, 0.4);
    }
}

.cst-loading-title {
    font-size: 18px;
    font-weight: 700;
    color: #0F172A;
    margin-top: 18px;
    margin-bottom: 6px;
}

.cst-loading-desc {
    font-size: 13.5px;
    color: #64748B;
    margin-bottom: 0;
    max-width: 480px;
}

.cst-loading-shimmer-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 100%;
    max-width: 320px;
}

.cst-loading-shimmer-line {
    height: 8px;
    border-radius: 4px;
    background: linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%);
    background-size: 200% 100%;
    animation: cst-shimmer 1.5s infinite linear;

    &--wide {
        width: 100%;
    }

    &--med {
        width: 60%;
    }
}

@keyframes cst-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}

/* ════════════════════════════════════════════════
   RESPONSIVE DESIGN
════════════════════════════════════════════════ */
@media (max-width: 768px) {
    .cst-nav {
        padding: 0 16px;
    }

    .cst-page {
        padding: 24px 16px 60px;
    }

    .cst-hero-title {
        font-size: 24px;
    }

    .cst-filter-box {
        padding: 16px;
    }

    .cst-modality-guide__banner {
        flex-direction: column;
        align-items: flex-start;
    }

    .cst-guide-card--pro {
        flex-direction: column;
    }
}
</style>
